import React from 'react';
import { type ApolloClient } from 'apollo-client';
import memoize from 'memoize-one';
import { dequal } from 'dequal/lite';
import { type ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import {
	type AnalyticsWebClient,
	type ForgeUIAnalyticsContext,
	sendEvent,
	extensionIdToAnalyticsAttributes,
} from '@atlassian/forge-ui';
import {
	type ContextId,
	type ProductEnvironment,
	type ForgeUIExtensionType,
} from '@atlassian/forge-ui-types';
import { type ExtensionHandlerWithReferenceFn } from '@atlassian/editor-referentiality';
import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import ForgeEditorExtension from './ForgeEditorExtension';
import { type ReferenceEntity } from '@atlaskit/editor-common/extensions';
import {
	type ConnectToForgeParameters,
	type CustomEditContext,
	type ForgeExtension,
	type ForgeExtensionParameters,
	type EditorAPI,
} from '../types';
import {
	P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE,
	CONNECT_ADDON_ACTION_SUBJECT,
} from '../utils/constants';
import { type FlagFunctions } from '../utils/getFlagProvider';
import { getConnectMacroId } from '../utils/getConnectMacroId';
import { fg } from '@atlaskit/platform-feature-flags';

interface GetExtensionParams {
	node: ForgeExtension;
	references?: Array<ReferenceEntity>;
}
interface RenderExtension {
	accountId?: string;
	analyticsWebClient: AnalyticsWebClient | Promise<AnalyticsWebClient>;
	apolloClient: ApolloClient<object>;
	cloudId: string;
	contextIds: ContextId[];
	customEditContext?: CustomEditContext;
	dataProviders: ProviderFactory;
	editorAPI?: EditorAPI;
	environment: ProductEnvironment;
	extension?: ForgeUIExtensionType;
	extensionData: Record<string, any>;
	extensionHandlerWithReference?: ExtensionHandlerWithReferenceFn<ForgeExtensionParameters>;
	FlagProvider: ({ children }: { children: (flags: FlagFunctions) => JSX.Element }) => JSX.Element;
	forgeUIAnalyticsContext: ForgeUIAnalyticsContext;
	hideGlassPane?: boolean;
	isEditing: boolean;
	page: string;
	product: string;
	useGetContextToken?: (
		extension: ForgeUIExtensionType | undefined,
		extensionData: RenderExtension['extensionData'],
		localId: string | undefined,
		contextIds: string[],
		cachingEnabled: boolean,
	) => (contextIdsOverride?: string[]) => Promise<string>;
	useRouteActions?: () => {
		[key: string]: (...args: any[]) => any;
		push: (url: string, forceReload?: boolean) => void;
	};
}

type ContextTokenHandlerWrapperProps = {
	children: (
		getContextTokenHandler: (contextIdsOverride?: string[]) => Promise<string>,
	) => JSX.Element;
	contextIds: string[];
	extension: ForgeUIExtensionType | undefined;
	extensionData: RenderExtension['extensionData'];
	localId: string | undefined;
	useGetContextToken: (
		extension: ForgeUIExtensionType | undefined,
		extensionData: RenderExtension['extensionData'],
		localId: string | undefined,
		contextIds: string[],
		cachingEnabled: boolean,
	) => (contextIdsOverride?: string[]) => Promise<string>;
};

function ContextTokenHandlerWrapper({
	useGetContextToken,
	extension,
	extensionData,
	localId,
	contextIds,
	children,
}: ContextTokenHandlerWrapperProps) {
	const getContextTokenHandler = useGetContextToken(extension, extensionData, localId, contextIds, true);
	return children(getContextTokenHandler);
}

export const renderExtension = ({
	accountId,
	apolloClient,
	analyticsWebClient,
	cloudId,
	contextIds,
	environment,
	extension,
	extensionData,
	extensionHandlerWithReference,
	forgeUIAnalyticsContext,
	isEditing,
	page,
	product,
	dataProviders,
	FlagProvider,
	hideGlassPane,
	editorAPI,
	customEditContext,
	useGetContextToken,
	useRouteActions,
}: RenderExtension) =>
	memoize(({ node, references }: GetExtensionParams) => {
		const extensionWithParam = {
			...node,
			parameters: {
				// we always insert with parameters
				...node.parameters!,
				extensionTitle: extension?.properties.title,
				extension,
			},
		};

		// If `localId` doesn't exist at this point, pass the `localId` on the node created from `quickInsert` into the parameters object
		// Required to render the correct config sidebar panel
		if (node.parameters && !node.parameters.localId) {
			node.parameters.localId = node.localId as string;
		}

		const augmentedExtensionData: RenderExtension['extensionData'] = {
			...extensionData,
			autoConvertLink: node.parameters?.autoConvertLink,
		};
		const connectMetadata = getConnectMacroMetadata(node.parameters!);
		if (connectMetadata) {
			augmentedExtensionData.connectMetadata = connectMetadata;
		}

		const getForgeEditorExtensionComponent = ({ references }: GetExtensionParams) => (
			<FlagProvider>
				{(flags: FlagFunctions) =>
					useGetContextToken && fg('forge_ui_use_get_context_token') ? (
						<ContextTokenHandlerWrapper
							useGetContextToken={useGetContextToken}
							extension={extensionWithParam?.parameters?.extension}
							extensionData={augmentedExtensionData}
							localId={node.parameters?.localId}
							contextIds={contextIds}
						>
							{(getContextTokenHandler) => (
								<ForgeEditorExtension
									analyticsWebClient={analyticsWebClient}
									accountId={accountId || ''}
									apolloClient={apolloClient}
									cloudId={cloudId}
									contextIds={contextIds}
									environment={environment}
									extension={transformConnectToForgeExtension(extensionWithParam, extension, {
										analyticsWebClient,
										forgeUIAnalyticsContext,
										isEditing,
									})}
									extensionData={{
										...augmentedExtensionData,
										references,
									}}
									flags={flags}
									isEditing={isEditing}
									product={product}
									dataProviders={dataProviders}
									page={page}
									hideGlassPane={hideGlassPane}
									editorAPI={editorAPI}
									getContextToken={getContextTokenHandler}
									customEditContext={customEditContext}
									useRouteActions={useRouteActions}
								/>
							)}
						</ContextTokenHandlerWrapper>
					) : (
						<ForgeEditorExtension
							analyticsWebClient={analyticsWebClient}
							accountId={accountId || ''}
							apolloClient={apolloClient}
							cloudId={cloudId}
							contextIds={contextIds}
							environment={environment}
							extension={transformConnectToForgeExtension(extensionWithParam, extension, {
								analyticsWebClient,
								forgeUIAnalyticsContext,
								isEditing,
							})}
							extensionData={{
								...augmentedExtensionData,
								references,
							}}
							flags={flags}
							isEditing={isEditing}
							product={product}
							dataProviders={dataProviders}
							page={page}
							hideGlassPane={hideGlassPane}
							editorAPI={editorAPI}
							customEditContext={customEditContext}
							useRouteActions={useRouteActions}
						/>
					)
				}
			</FlagProvider>
		);

		if (extensionHandlerWithReference) {
			return extensionHandlerWithReference(node, ({ references: referencesFromHandler }) =>
				getForgeEditorExtensionComponent({
					node,
					references: referencesFromHandler,
				}),
			);
		}

		return getForgeEditorExtensionComponent({ node, references });
	}, dequal);

function getConnectMacroMetadata(parameters: ConnectToForgeParameters):
	| {
			macroId: string;
			title?: string;
	  }
	| undefined {
	const macroId = getConnectMacroId(parameters);
	const title = parameters?.macroMetadata?.title;
	if (!macroId) {
		// We don't want this function to return anything if it didn't have a Connect macro ID,
		// to avoid implying that pure Forge macros were Connect
		return undefined;
	}
	return {
		macroId,
		title,
	};
}

function transformConnectToForgeExtension(
	attrs: ForgeExtension,
	extension?: ForgeUIExtensionType,
	analyticsProps?: Pick<
		RenderExtension,
		'analyticsWebClient' | 'forgeUIAnalyticsContext' | 'isEditing'
	>,
): ForgeExtension {
	if (attrs.extensionType !== P2_OR_CONNECT_CONFLUENCE_MACRO_EXTENSION_TYPE) {
		return attrs;
	}

	const { analyticsWebClient, forgeUIAnalyticsContext, isEditing } = analyticsProps || {};
	const eventSource = isEditing ? 'editPageScreen' : 'viewPageScreen';
	const { extensionKey } = attrs;

	try {
		if (!extension?.id) {
			throw new Error('No Forge extension for patching connect addon');
		}
		if (attrs.parameters) {
			if (!attrs.parameters.extensionId) {
				attrs.parameters.extensionId = extension.id;
			}
			const macroId = getConnectMacroId(attrs.parameters);
			if (!attrs.parameters.localId && macroId) {
				// Populate with Connect macroId
				attrs.parameters.localId = macroId;
			}
		}

		// Transform Connect macroParams to Forge guestParams
		if (attrs.parameters?.macroParams) {
			attrs.parameters.guestParams = attrs.parameters.guestParams || {};
			const connectParams = attrs.parameters.macroParams;
			Object.entries(connectParams).forEach(([key, obj]: [string, any]) => {
				if (typeof attrs.parameters!.guestParams![key] === 'undefined') {
					attrs.parameters!.guestParams![key] = obj.value;
				}
			});
		}
		if (analyticsWebClient) {
			sendEvent(analyticsWebClient)({
				source: eventSource,
				...forgeUIAnalyticsContext,
				eventType: OPERATIONAL_EVENT_TYPE,
				action: 'h11n_macro_render_success',
				actionSubject: CONNECT_ADDON_ACTION_SUBJECT,
				actionSubjectId: extensionKey,
				attributes: {
					extensionKey,
					...extensionIdToAnalyticsAttributes(extension?.id),
					nodeLocalId: attrs.localId,
				},
			});
		}
	} catch (e) {
		if (analyticsWebClient) {
			sendEvent(analyticsWebClient)({
				source: eventSource,
				...forgeUIAnalyticsContext,
				eventType: OPERATIONAL_EVENT_TYPE,
				action: 'h11n_macro_render_failed',
				actionSubject: CONNECT_ADDON_ACTION_SUBJECT,
				actionSubjectId: extensionKey,
				attributes: {
					extensionKey,
					...extensionIdToAnalyticsAttributes(extension?.id),
					errorMessage: e instanceof Error ? e.message : String(e),
					nodeLocalId: attrs.localId,
				},
			});
		}
		throw e;
	}

	return attrs;
}
